/** Saved preset helpers: canonical persisted-profile comparison (dirty state),
 * preset naming, and ordering. */

// Filter sections the built-in (system) presets set. Editing any of these
// after applying a system preset turns the active preset into "Custom";
// retailer/category/name changes do not.
export const SYSTEM_PRESET_SECTIONS = [
  "match_strategy", "exclusions", "profitability", "demand", "competition",
];

const PERSISTED_TOP_LEVEL = [
  "name", "mode", "scope", "match_strategy", "adjustments", "exclusions", "title_rules",
  "costs", "profitability", "demand", "competition", "availability", "scan_limits",
  "schedule", "enabled",
];

// Execution fields derived from scope.category_selection by the serializer.
const DERIVED_SCOPE_FIELDS = ["selected_categories", "categories", "custom_category_query"];

function normalizeValue(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    const items = value.map(normalizeValue).filter((v) => v !== undefined);
    return items.every((v) => typeof v !== "object" || v === null)
      ? [...items].sort((a, b) => String(a).localeCompare(String(b)))
      : items;
  }
  if (typeof value === "object") {
    const out = {};
    Object.keys(value)
      .filter((k) => !k.startsWith("_"))
      .sort()
      .forEach((k) => {
        const v = normalizeValue(value[k]);
        if (v !== undefined) out[k] = v;
      });
    return out;
  }
  return value;
}

function categoryIdentity(selection) {
  if (!selection || typeof selection !== "object") return null;
  if (selection.mode === "category") return { mode: "category", category_id: selection.category_id || null };
  if (selection.mode === "custom") return { mode: "custom", user_query: String(selection.user_query || "").trim() };
  if (selection.mode === "all") return { mode: "all" };
  return null;
}

/** Canonical representation of the persisted profile fields only (a
 * serialized payload in, a plain sorted object out). UI-only state never
 * reaches the payload; derived category fields collapse to the selection. */
export function normalizePersistedProfile(payload) {
  const out = {};
  for (const key of PERSISTED_TOP_LEVEL) {
    if (payload?.[key] !== undefined) out[key] = payload[key];
  }
  out.name = String(out.name || "").split(/\s+/).filter(Boolean).join(" ");
  if (out.scope) {
    const scope = { ...out.scope };
    DERIVED_SCOPE_FIELDS.forEach((k) => delete scope[k]);
    scope.category_selection = categoryIdentity(scope.category_selection);
    out.scope = scope;
  }
  out.enabled = Boolean(out.enabled);
  return normalizeValue(out);
}

export function stableStringify(value) {
  return JSON.stringify(normalizeValue(value));
}

export function sameProfile(a, b) {
  return stableStringify(a) === stableStringify(b);
}

export function systemPresetSignature(profile) {
  const out = {};
  SYSTEM_PRESET_SECTIONS.forEach((k) => { out[k] = profile?.[k] ?? null; });
  return stableStringify(out);
}

/** ``base`` if free, else ``base 2``, ``base 3``… */
export function uniquePresetName(base, existingNames = []) {
  const taken = new Set(existingNames.map((n) => String(n || "").trim().toLowerCase()));
  const clean = String(base || "").trim() || "Preset";
  if (!taken.has(clean.toLowerCase())) return clean;
  let n = 2;
  while (taken.has(`${clean} ${n}`.toLowerCase())) n += 1;
  return `${clean} ${n}`;
}

/** Name for a new preset: typed name wins; otherwise the retailer/category
 * name, prefixed by the system preset it came from (not for "All"). */
export function generatedPresetName({ typedName, autoName, systemPresetName, existingNames = [] }) {
  const typed = String(typedName || "").trim();
  if (typed) return typed;
  const base = String(autoName || "").trim() || "Auto Sourcing search";
  const named = systemPresetName && systemPresetName !== "All" ? `${systemPresetName} — ${base}` : base;
  return uniquePresetName(named, existingNames);
}

export const PRESET_NAME_MAX = 160;

export function validatePresetName(name) {
  const clean = String(name || "").split(/\s+/).filter(Boolean).join(" ");
  if (!clean) return { ok: false, error: "Preset name can't be blank." };
  if (clean.length > PRESET_NAME_MAX) return { ok: false, error: `Keep the name under ${PRESET_NAME_MAX} characters.` };
  return { ok: true, name: clean };
}

/** Default preset first, then newest first (stable across Save Changes). */
export function sortPresets(presets) {
  return [...presets].sort((a, b) => {
    if (Boolean(b.is_default) !== Boolean(a.is_default)) return b.is_default ? 1 : -1;
    return String(b.created_at || "").localeCompare(String(a.created_at || ""));
  });
}
